import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useReactTable, getCoreRowModel } from '@tanstack/react-table'
import DataTable, { DataTableColumnHeader, DataTablePagination } from '../../ui/DataTable'
import EmptyState from '../../ui/EmptyState'
import ConfiguredAlertsRow from './ConfiguredAlertsRow'

const EMPTY_MESSAGE = 'Les alertes configurées apparaîtront ici une fois créées.'

const EMPTY_ALERTS = []
const EMPTY_PAGY = { page: 1, pages: 0, count: 0, items: 0 }

const EMPTY_STATE = <EmptyState message={EMPTY_MESSAGE} title="Aucune donnée" />

// Recipient sorting is not supported by the backend (multi-user association),
// So enableSorting:false on that column only.
const renderNameHeader = ({ column }) => <DataTableColumnHeader column={column} title="Nom de l’alerte" />
const renderDateHeader = ({ column }) => <DataTableColumnHeader column={column} title="Date de création" />
const renderRecipientsHeader = () => <span>Destinataire</span>

const COLUMNS = [
  { id: 'name', accessorKey: 'name', enableSorting: true, header: renderNameHeader },
  { id: 'created_at', accessorKey: 'created_at', enableSorting: true, header: renderDateHeader },
  { id: 'recipients', enableSorting: false, header: renderRecipientsHeader }
]

const ConfiguredAlertsTable = ({
  alerts = EMPTY_ALERTS,
  pagy = EMPTY_PAGY,
  loading = false,
  error = null,
  sort = null,
  direction = 'desc',
  isAlertMutating = null,
  onDuplicate, onEdit, onDelete, onPageChange, onPerPageChange, onSortChange
}) => {
  const sortingState = useMemo(
    () => sort ? [{ id: sort, desc: direction === 'desc' }] : [], [sort, direction]
  )

  const handleSortingChange = useCallback(updater => {
    const next = typeof updater === 'function' ? updater(sortingState) : updater
    if (next.length === 0) {
      onSortChange(null, 'desc')
      return
    }
    const [{ id, desc }] = next
    onSortChange(id, desc ? 'desc' : 'asc')
  }, [sortingState, onSortChange])

  const table = useReactTable({
    data: alerts,
    columns: COLUMNS,
    state: { sorting: sortingState },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: row => String(row.id)
  })

  const renderRow = useCallback(row => (
    <ConfiguredAlertsRow
      key={row.id}
      alert={row.original}
      disabled={isAlertMutating ? isAlertMutating(row.original.id) : false}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onEdit={onEdit}
    />
  ), [isAlertMutating, onDelete, onDuplicate, onEdit])

  const pagination = useMemo(() => (
    <DataTablePagination
      disabled={loading}
      pagy={pagy}
      onPageChange={onPageChange}
      onPerPageChange={onPerPageChange}
    />
  ), [loading, pagy, onPageChange, onPerPageChange])

  return (
    <DataTable
      emptyState={EMPTY_STATE}
      error={error}
      loading={loading}
      pagination={pagination}
      renderRow={renderRow}
      table={table}
    />
  )
}

ConfiguredAlertsTable.propTypes = {
  alerts: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number.isRequired })),
  direction: PropTypes.oneOf(['asc', 'desc']),
  error: PropTypes.instanceOf(Error),
  isAlertMutating: PropTypes.func,
  loading: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  pagy: PropTypes.shape({
    count: PropTypes.number,
    items: PropTypes.number,
    page: PropTypes.number,
    pages: PropTypes.number
  }),
  sort: PropTypes.string
}

ConfiguredAlertsTable.defaultProps = {
  alerts: EMPTY_ALERTS,
  direction: 'desc',
  error: null,
  isAlertMutating: null,
  loading: false,
  pagy: EMPTY_PAGY,
  sort: null
}

export default ConfiguredAlertsTable
